import styles from './master-detail-table.module.scss';
import { Col, Row } from 'reactstrap';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AgGridReact } from 'ag-grid-react';
import { GridApi, ColumnApi, GridReadyEvent, PaginationChangedEvent } from 'ag-grid-community';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';

import { Pagination } from '../pagination/pagination';
import { Icon, TextField } from '@frontend-workspace/react-shared-lib';
import { AG_GRID_LOCALE_NBNO } from '../simple-table/ag-grid.locale.nb';

import { InputAdornment } from '@mui/material';

/* eslint-disable-next-line */
export interface MasterDetailTableProps {
  data: Array<any>,
  masterHeaders: Array<any>,
  detailHeaders: Array<any>,
  detailsPropertyName: string,
  hideSearch?: boolean,
  fitHeight?: boolean,
  hasColumnFilters?: boolean,
  isRowMaster?: (dataItem: any) => boolean,
}

export function MasterDetailTable(props: MasterDetailTableProps) {
  const gridRef = useRef() as any;
  const { t } = useTranslation();
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | null>(null);
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
  const gridStyle = useMemo(() => {
    if (!props.fitHeight) {
      var height = props.data.length * 42 + 48;
      return ({ height: height, width: '100%' })
    }
    return ({ height: '100%', width: '100%' })
  }, [props.data])

  const [currentPage, setCurrentPage] = useState<number>();
  const [pageCount, setPageCount] = useState<number>();
  //const [columnDefs, setColumnDefs] = useState(props.masterHeaders);
  const detailRowClass = styles.detailRowStyle;

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      resizable: true,
      filter: props.hasColumnFilters,
      sortable: true
    };
  }, []);
  const detailCellRendererParams = useMemo(() => {
    return {
      detailGridOptions: {
        columnDefs: props.detailHeaders,
        rowClass: detailRowClass,
        localeText: AG_GRID_LOCALE_NBNO,
        defaultColDef: {
          flex: 1,
        }
      },
      getDetailRowData: function (params: any) {
        params.successCallback(params.data[props.detailsPropertyName]);
      }
    };
  }, [props.detailHeaders]);


  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  }, []);

  const onFirstDataRendered = useCallback((params: any) => {
    gridPaginationChanged();
  }, []);

  const muiPaginationChanged = (e: any, page: number) => {
    gridApi?.paginationGoToPage(page - 1);
  }

  const gridPaginationChanged = (e?: PaginationChangedEvent) => {
    setCurrentPage((gridApi?.paginationGetCurrentPage() || 0) + 1);
    setPageCount(gridApi?.paginationGetTotalPages() || 1);
  }

  const search = (e: any) => {
    gridApi?.setQuickFilter(e.target.value);
  }

  const getContextMenuItems = useCallback((params: any) => {
    return [
      'copy',
      'export'
    ];
  }, []);

  return (
    <div>
      <div className="ag-theme-alpine" data-testid="ag-theme-alpine-master-detail-table">
        {!props.hideSearch && <Row className="mb-3 justify-content-end">
          <Col xs={12} sm={8} className='d-flex justify-content-start justify-content-sm-end'>
            <div>
              <TextField
                onChange={search}
                placeholder={t('simpleTable.searchInTable') || ""}
                InputProps={{
                  startAdornment:
                    <InputAdornment position="start">
                      <Icon>search</Icon>
                    </InputAdornment>
                }}
              />
            </div>
          </Col>
        </Row>}
        <AgGridReact
          localeText={AG_GRID_LOCALE_NBNO}
          ref={gridRef}
          rowData={props.data}
          columnDefs={props.masterHeaders}
          defaultColDef={defaultColDef}
          masterDetail={true}
          pagination={true}
          paginationPageSize={10}
          //rowSelection={'single'}
          enableCellTextSelection={true}
          suppressPaginationPanel={true}
          onPaginationChanged={gridPaginationChanged}
          detailCellRendererParams={detailCellRendererParams}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
          suppressColumnVirtualisation={true}
          detailRowAutoHeight={true}
          domLayout={'autoHeight'}
          isRowMaster={props.isRowMaster}
          getContextMenuItems={getContextMenuItems}>
        </AgGridReact>
        {props.data &&
          <Pagination
            className="d-flex justify-content-end mt-4"
            count={pageCount || 0}
            page={currentPage || 0}
            onChange={(e, page) => muiPaginationChanged(e, page)}
          />
        }
      </div>
    </div>
  );
}

export default MasterDetailTable;